import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { ArrowRight, CheckCircle, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CATEGORIES = ['Food & Drink', 'Nightlife', 'Fitness & Wellness', 'Activities', 'Free Deals', 'Convenience Stores', 'Liquor Stores'];

export default function BusinessRegisterPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [existingBusiness, setExistingBusiness] = useState(null);
  const [checkingBusiness, setCheckingBusiness] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const [form, setForm] = useState({
    name: '', description: '', category: 'Food & Drink',
    city_id: 'athens_ga', address: '', zip_code: '', phone: '', website: '', email: '',
    logo_url: '', owner_name: '', tax_id: '',
    requires_age_verification: false,
  });

  useEffect(() => {
    fetch(`${API}/cities`)
      .then((r) => r.json())
      .then((data) => setCities(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    setCheckingBusiness(true);
    fetch(`${API}/businesses/me`, { credentials: 'include' })
      .then((r) => { if (r.ok) return r.json(); throw new Error('none'); })
      .then((b) => setExistingBusiness(b))
      .catch(() => setExistingBusiness(null))
      .finally(() => setCheckingBusiness(false));
    if (user.email) setForm((f) => ({ ...f, email: user.email }));
  }, [user]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { setShowAuthModal(true); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/businesses/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        // Specific rejection message for duplicate EIN
        const msg = data.detail || 'Registration failed';
        if (msg.toLowerCase().includes('tax id') || msg.toLowerCase().includes('ein')) {
          setError('REJECTED: ' + msg);
        } else {
          setError(msg);
        }
        return;
      }
      setSuccess(true);
      await refreshUser();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <SEO
        rawTitle="List Your Business on Perkly | Reach Athens Students"
        description="Partner with Perkly to promote your Athens business to thousands of local students. Create exclusive deals, track redemptions, and grow your customer base."
        canonical="/business/register"
      />
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Left — Info */}
          <div className="lg:sticky lg:top-24">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-4">Grow Your Business</p>
            <h1
              className="text-4xl lg:text-5xl font-bold text-[#2B2B2B] leading-tight mb-5"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="business-register-title"
            >
              List Your Business on Perkly
            </h1>
            <p className="text-base text-[#2B2B2B]/65 leading-relaxed mb-8">
              Reach thousands of students and locals in your city. Add your deal, grow your customer base, and increase foot traffic — all for free.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Free to join', desc: 'No cost to list your first deal on Perkly' },
                { title: 'Reach students', desc: 'Connect with an engaged audience of 18-28 year olds' },
                { title: 'Quick setup', desc: 'Be live and discoverable in under 24 hours' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-[#C8A97E] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-sm text-[#2B2B2B]">{item.title}</p>
                    <p className="text-xs text-[#2B2B2B]/55">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <img
              src="https://images.unsplash.com/photo-1594404430367-9858af713541?w=600&q=80"
              alt="Business owner"
              className="w-full rounded-3xl object-cover h-[260px] shadow-lg"
            />
          </div>

          {/* Right — Form */}
          <div className="bg-white rounded-3xl p-8 border border-[#8A9A8B]/10 shadow-sm">
            {checkingBusiness ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : success ? (
              <div className="text-center py-8" data-testid="business-register-success">
                {/* Auto-approved immediately — no waiting */}
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 border border-green-200 rounded-full px-4 py-1.5 text-xs font-bold mb-4">
                  ✓ Auto-Approved
                </div>
                <h2 className="text-2xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Business Approved!
                </h2>
                <p className="text-[#2B2B2B]/60 mb-2 leading-relaxed text-sm">
                  Your Tax ID / EIN was verified as unique. Your business is <strong>live on Perkly right now</strong> — no waiting required.
                </p>
                <p className="text-xs text-[#8A9A8B] mb-6">A confirmation email has been sent to your registered email.</p>
                <button onClick={() => navigate('/business/dashboard')} className="btn-primary inline-flex">
                  Go to Dashboard <ArrowRight size={14} />
                </button>
              </div>
            ) : existingBusiness ? (
              <div className="text-center py-8" data-testid="business-already-registered">
                <div className="w-16 h-16 rounded-full bg-[#8A9A8B]/15 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={32} className="text-[#8A9A8B]" />
                </div>
                <h2 className="text-2xl font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {existingBusiness.name}
                </h2>
                <p className="text-sm text-[#2B2B2B]/60 mb-2">
                  Status: <span className={`font-semibold ${existingBusiness.status === 'approved' ? 'text-green-600' : existingBusiness.status === 'rejected' ? 'text-red-500' : 'text-[#C8A97E]'}`}>
                    {existingBusiness.status.charAt(0).toUpperCase() + existingBusiness.status.slice(1)}
                  </span>
                </p>
                {existingBusiness.status === 'pending' && (
                  <p className="text-xs text-[#2B2B2B]/50 mb-6">Your application is being processed — this should resolve automatically. Contact support if it persists.</p>
                )}
                <button onClick={() => navigate('/business/dashboard')} className="btn-primary inline-flex">
                  Go to Dashboard <ArrowRight size={14} />
                </button>
              </div>
            ) : !user ? (
              <div className="text-center py-8" data-testid="business-signin-prompt">
                <div className="bg-[#C8A97E]/10 border border-[#C8A97E]/25 rounded-2xl p-5 mb-5">
                  <Shield size={24} className="text-[#C8A97E] mx-auto mb-2" />
                  <p className="text-sm font-semibold text-[#2B2B2B]">Sign in to register your business</p>
                  <p className="text-xs text-[#2B2B2B]/55 mt-1 leading-relaxed">Perkly verifies all business listings. Create an account to get started.</p>
                </div>
                <button data-testid="business-signin-button" onClick={() => setShowAuthModal(true)} className="btn-primary inline-flex">
                  Sign In / Create Account <ArrowRight size={14} />
                </button>
                {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} returnTo="/business/register" />}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="business-register-form">
                <h2 className="text-xl font-bold text-[#2B2B2B] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Business Information
                </h2>

                {error && (
                  error.startsWith('REJECTED:') ? (
                    /* EIN duplicate — show rejection banner */
                    <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-5" data-testid="business-register-error">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-lg">🚫</span>
                        </div>
                        <div>
                          <p className="font-bold text-red-700 text-base mb-1">Application Rejected</p>
                          <p className="text-red-600 text-sm leading-relaxed">
                            {error.replace('REJECTED: ', '')}
                          </p>
                          <p className="text-red-500/70 text-xs mt-2">
                            If you believe this is an error, contact <a href="mailto:support@useperkly.com" className="underline">support@useperkly.com</a>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl p-3" data-testid="business-register-error">
                      {error}
                    </div>
                  )
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Business Name *</label>
                    <input name="name" required value={form.name} onChange={handleChange} placeholder="e.g. The Grill House" className="perkly-input" data-testid="business-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Category *</label>
                    <select name="category" value={form.category} onChange={handleChange} className="perkly-input" data-testid="business-category-select">
                      {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Age verification — shown when Liquor Stores is selected */}
                {form.category === 'Liquor Stores' && (
                  <div className="rounded-2xl bg-[#8B1A1A]/6 border border-[#8B1A1A]/20 p-4" data-testid="age-verification-section">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.requires_age_verification}
                        onChange={(e) => setForm({ ...form, requires_age_verification: e.target.checked })}
                        className="mt-0.5 w-4 h-4 rounded accent-[#8B1A1A] flex-shrink-0"
                        data-testid="age-verification-checkbox"
                      />
                      <div>
                        <p className="text-sm font-bold text-[#8B1A1A] leading-snug">This business sells age-restricted products (21+)</p>
                        <p className="text-xs text-[#2B2B2B]/60 mt-1 leading-relaxed">
                          Checking this box will display a <strong>21+ Only</strong> badge on all your deals and require your staff to verify customer ID at time of redemption. Age verification compliance is entirely the responsibility of the business.
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Owner Full Name *</label>
                    <input name="owner_name" required value={form.owner_name} onChange={handleChange} placeholder="Your legal name" className="perkly-input" data-testid="business-owner-input" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">
                      Tax ID / EIN *
                      <span className="ml-1 text-[#C8A97E] normal-case font-normal">Required for verification</span>
                    </label>
                    <input name="tax_id" required value={form.tax_id} onChange={handleChange} placeholder="XX-XXXXXXX (EIN format)" className="perkly-input font-mono" data-testid="business-tax-id-input" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Description *</label>
                  <textarea name="description" required value={form.description} onChange={handleChange} rows={3} placeholder="Tell customers what makes your business special..." className="perkly-input resize-none" data-testid="business-description-input" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">City *</label>
                    <select name="city_id" value={form.city_id} onChange={handleChange} className="perkly-input" data-testid="business-city-select">
                      {cities.map((c) => <option key={c.city_id} value={c.city_id}>{c.full_name || `${c.name}, ${c.state}`}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Business Email *</label>
                    <input name="email" type="email" required value={form.email} onChange={handleChange} placeholder="hello@yourbusiness.com" className="perkly-input" data-testid="business-email-input" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Address *</label>
                    <input name="address" required value={form.address} onChange={handleChange} placeholder="123 Main St" className="perkly-input" data-testid="business-address-input" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">ZIP Code</label>
                    <input name="zip_code" value={form.zip_code} onChange={handleChange} placeholder="30601" className="perkly-input" data-testid="business-zip-input" />
                    <p className="text-xs text-[#8A9A8B] mt-1">For map placement</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Phone *</label>
                    <input name="phone" required value={form.phone} onChange={handleChange} placeholder="706-555-0100" className="perkly-input" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Website</label>
                    <input name="website" value={form.website} onChange={handleChange} placeholder="https://yourbusiness.com" className="perkly-input" />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="business-register-submit"
                  className="w-full btn-primary justify-center py-4 text-base disabled:opacity-60"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
                <p className="text-xs text-center text-[#2B2B2B]/45">
                  Your business will be reviewed and approved by our team within 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
